import React, { useState, useRef, useCallback } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import { uploadPrescription, uploadReport } from '../../services/api/visitMetadataApi';

/**
 * Allowed file extensions (mirrors backend ALLOWED_EXTENSIONS).
 */
const ALLOWED_EXTENSIONS = new Set([
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
  '.txt', '.csv', '.rtf', '.odt', '.ods',
  '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg',
  '.heic', '.heif', '.tiff', '.tif',
]);

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

/**
 * AddPrescription — File upload component for prescriptions and reports.
 *
 * Two upload methods:
 *  1. File picker (browse)
 *  2. Camera capture (mobile-friendly)
 *
 * Props:
 *  - visitId: string
 *  - metaType: 'PRESCRIPTION' | 'REPORT'
 *  - onSuccess: (msg) => void
 *  - onClose: () => void
 */
export default function AddPrescription({ visitId, metaType = 'PRESCRIPTION', onSuccess, onClose }) {
  const { isDark } = useTheme();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null); // { file, url, name }
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const isPrescription = metaType === 'PRESCRIPTION';
  const label = isPrescription ? 'Prescription' : 'Report';

  const validateFile = useCallback((file) => {
    if (!file) return 'No file selected.';
    const ext = '.' + (file.name || '').split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.has(ext)) {
      return `File type '${ext}' is not supported. Use PDF, DOC, or image files.`;
    }
    if (file.size === 0) return 'File is empty.';
    if (file.size > MAX_FILE_SIZE) return 'File too large (max 10 MB).';
    return null;
  }, []);

  const handleFileSelected = useCallback((file) => {
    setError(null);
    const err = validateFile(file);
    if (err) {
      setError(err);
      return;
    }
    // Create preview for images
    const isImage = file.type?.startsWith('image/');
    const url = isImage ? URL.createObjectURL(file) : null;
    setPreview({ file, url, name: file.name, isImage });
  }, [validateFile]);

  const handleFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelected(file);
    // Reset input so same file can be re-selected
    e.target.value = '';
  }, [handleFileSelected]);

  const handleUpload = useCallback(async () => {
    if (!preview?.file) return;
    setUploading(true);
    setError(null);
    try {
      const uploadFn = isPrescription ? uploadPrescription : uploadReport;
      await uploadFn(visitId, preview.file);
      // Cleanup preview URL
      if (preview.url) URL.revokeObjectURL(preview.url);
      setPreview(null);
      onSuccess?.(`${label} uploaded successfully`);
    } catch (err) {
      setError(err.message || `Failed to upload ${label.toLowerCase()}.`);
    } finally {
      setUploading(false);
    }
  }, [preview, isPrescription, visitId, onSuccess, label]);

  const handleCancel = useCallback(() => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
    setError(null);
  }, [preview]);

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div style={{ padding: '0.75rem 1rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '0.75rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
          <span style={{ fontSize: '1rem' }}>{isPrescription ? '💊' : '📄'}</span>
          <span style={{
            fontSize: '0.8125rem', fontWeight: 600,
            color: isDark ? '#f5f5f7' : '#111827',
          }}>
            Upload {label}
          </span>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '0.875rem', color: isDark ? '#6b7280' : '#9ca3af',
              padding: '0.125rem',
            }}
          >
            ✕
          </button>
        )}
      </div>

      {/* Upload Buttons — shown when no file is selected */}
      {!preview && (
        <div style={{
          display: 'flex', gap: '0.625rem', flexWrap: 'wrap',
        }}>
          {/* File Upload Button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            style={{
              flex: 1, minWidth: '120px',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              gap: '0.375rem', padding: '1rem 0.75rem',
              borderRadius: '0.75rem',
              border: `2px dashed ${isDark ? 'rgba(13, 148, 136,0.3)' : 'rgba(13, 148, 136,0.25)'}`,
              background: isDark ? 'rgba(13, 148, 136,0.06)' : 'rgba(13, 148, 136,0.03)',
              color: isDark ? '#5eead4' : '#0d9488',
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            <span style={{ fontSize: '1.5rem' }}>📁</span>
            <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>Upload File</span>
            <span style={{
              fontSize: '0.5625rem',
              color: isDark ? '#6b7280' : '#9ca3af',
            }}>
              PDF, DOC, Images
            </span>
          </button>

          {/* Camera Capture Button */}
          <button
            onClick={() => cameraInputRef.current?.click()}
            disabled={uploading}
            style={{
              flex: 1, minWidth: '120px',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              gap: '0.375rem', padding: '1rem 0.75rem',
              borderRadius: '0.75rem',
              border: `2px dashed ${isDark ? 'rgba(34,197,94,0.3)' : 'rgba(34,197,94,0.25)'}`,
              background: isDark ? 'rgba(34,197,94,0.06)' : 'rgba(34,197,94,0.03)',
              color: isDark ? '#86efac' : '#22c55e',
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            <span style={{ fontSize: '1.5rem' }}>📷</span>
            <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>Camera</span>
            <span style={{
              fontSize: '0.5625rem',
              color: isDark ? '#6b7280' : '#9ca3af',
            }}>
              Take a photo
            </span>
          </button>

          {/* Hidden inputs */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf,.odt,.ods,.jpg,.jpeg,.png,.gif,.bmp,.webp,.svg,.heic,.heif,.tiff,.tif"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </div>
      )}

      {/* File Preview — shown after file is selected */}
      {preview && (
        <div style={{
          borderRadius: '0.625rem',
          border: `1px solid ${isDark ? 'rgba(13, 148, 136,0.2)' : 'rgba(13, 148, 136,0.15)'}`,
          background: isDark ? 'rgba(13, 148, 136,0.05)' : 'rgba(13, 148, 136,0.02)',
          overflow: 'hidden',
        }}>
          {/* Image preview */}
          {preview.isImage && preview.url && (
            <div style={{
              display: 'flex', justifyContent: 'center',
              padding: '0.5rem',
              background: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)',
              maxHeight: '200px', overflow: 'hidden',
            }}>
              <img
                src={preview.url}
                alt="Preview"
                style={{
                  maxHeight: '180px', maxWidth: '100%',
                  objectFit: 'contain', borderRadius: '0.375rem',
                }}
              />
            </div>
          )}

          {/* File info */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '0.625rem 0.75rem',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: '1.25rem', flexShrink: 0 }}>
                {preview.isImage ? '🖼️' : '📄'}
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{
                  fontSize: '0.75rem', fontWeight: 600,
                  color: isDark ? '#e8e9ed' : '#374151',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {preview.name}
                </div>
                <div style={{
                  fontSize: '0.625rem',
                  color: isDark ? '#6b7280' : '#9ca3af',
                }}>
                  {formatSize(preview.file.size)}
                </div>
              </div>
            </div>
            <button
              onClick={handleCancel}
              disabled={uploading}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '0.75rem', color: '#ef4444', padding: '0.25rem',
                flexShrink: 0,
              }}
            >
              ✕ Remove
            </button>
          </div>

          {/* Upload action */}
          <div style={{
            display: 'flex', gap: '0.5rem',
            padding: '0.5rem 0.75rem 0.625rem',
            borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}`,
          }}>
            <button
              onClick={handleUpload}
              disabled={uploading}
              style={{
                flex: 1,
                padding: '0.5rem',
                borderRadius: '0.5rem',
                border: 'none',
                background: uploading
                  ? isDark ? '#374151' : '#d1d5db'
                  : 'linear-gradient(135deg, #0d9488 0%, #0d9488 100%)',
                color: '#fff',
                fontSize: '0.75rem',
                fontWeight: 600,
                cursor: uploading ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s',
              }}
            >
              {uploading ? '⏳ Uploading...' : `📤 Upload ${label}`}
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{
          marginTop: '0.5rem',
          padding: '0.5rem 0.75rem',
          borderRadius: '0.5rem',
          background: isDark ? 'rgba(239,68,68,0.1)' : '#fef2f2',
          border: `1px solid ${isDark ? 'rgba(239,68,68,0.2)' : '#fecaca'}`,
          display: 'flex', alignItems: 'center', gap: '0.375rem',
          fontSize: '0.6875rem',
          color: isDark ? '#fca5a5' : '#991b1b',
        }}>
          ⚠️ {error}
        </div>
      )}
    </div>
  );
}
